import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import type { EventsPage } from '@finder/contracts';
import { ApiClient, classifyLoadError, type LoadErrorKind } from './api-client.js';
import {
  locationReducer,
  loadSessionLocation,
  saveSessionLocation,
  clearSessionLocation,
  defaultLocationState,
  sameCoordinates,
  originPinLabel,
  type LocationState,
} from './location/location-service.js';
import { LocationPrompt } from './location/LocationPrompt.js';
import { ResultsView } from './results/ResultsView.js';
import {
  INITIAL_RESULTS_FOCUS,
  RESULTS_HEADING_ID,
  nextResultsFocus,
} from './results/results-focus.js';
import {
  Filters,
  DEFAULT_FILTER_STATE,
  filterChanges,
  type FilterState,
} from './filters/Filters.js';
import {
  fallbackNoticeFor,
  fallbackNoticeText,
  fallbackReturnLabel,
  isDefaultLanding,
  resolveEmptyFallback,
  type FallbackNotice,
  type FallbackScope,
} from './results/empty-fallback.js';
import {
  currentViewPath,
  initAnalytics,
  trackFilterChange,
  trackLocationSearch,
  trackPageView,
  trackUseMyLocation,
  trackWidenRadius,
} from './lib/analytics.js';
import { ConsentBanner } from './lib/ConsentBanner.js';
import { readConsentChoice, storeConsentChoice, type ConsentChoice } from './lib/consent.js';
import {
  LoadingState,
  ErrorState,
  EmptyState,
  WiderWindowNotice,
  StaleNotice,
  FreshnessIndicator,
  SourcesLine,
} from './states/States.js';
import { collectSourceNames } from './trust/presentation.js';

const api = new ApiClient(import.meta.env?.VITE_API_BASE ?? '/api/v1');

function queryKey(
  coords: { lat: number; lng: number },
  scope: Pick<FallbackScope, 'radiusMiles' | 'windowDays'>,
): string {
  return `${coords.lat},${coords.lng}|${scope.radiusMiles}|${scope.windowDays}`;
}

type Fetch = 'idle' | 'loading' | 'ok' | 'error';

/**
 * App shell. Browses from Seattle Center until the visitor chooses a place.
 * GPS runs only from the explicit "Use my location" control — never on load.
 * A chosen location is kept in memory + sessionStorage only; the default is not
 * stored.
 *
 * The redesign (UI-REDESIGN.md) adds: a first-run trust hero, a filter-chip bar
 * whose Distance/When drive the API query params, and richer result-state
 * surfaces. Offer, audience, confirmed-food, and "happening now" narrow the
 * returned page on the client. Sort stays on the server ranking unless the
 * visitor picks soonest or nearest. Issue #10 adds the sources line, an
 * always-on freshness stamp, and the deferred GPS ask.
 */
export function App(): JSX.Element {
  const storage = globalThis.sessionStorage;
  const [locState, dispatch] = useReducer(locationReducer, undefined, (): LocationState => {
    const restored = storage ? loadSessionLocation(storage, Date.now()) : null;
    return restored
      ? {
          status: 'ready',
          coordinates: restored.coordinates,
          source: restored.source,
          ...(restored.label ? { label: restored.label } : {}),
        }
      : defaultLocationState();
  });
  const locRef = useRef(locState);
  locRef.current = locState;
  // Bumps whenever a newer location choice should ignore an in-flight GPS or geocode.
  const requestGen = useRef(0);

  // The mount effect always queries the current origin, so the first paint is
  // the loading state rather than an empty results region.
  const [fetchState, setFetchState] = useState<Fetch>('loading');
  const [loadError, setLoadError] = useState<LoadErrorKind>('server');
  const [page, setPage] = useState<EventsPage | null>(null);
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTER_STATE);
  const [fallback, setFallback] = useState<FallbackNotice | null>(null);
  const [consent, setConsent] = useState<ConsentChoice | null>(() => readConsentChoice());
  // Ignores a slower automatic chain after the visitor picks another search.
  const loadGen = useRef(0);
  // The query already on screen, so syncing chips to an automatic wider window does not refetch.
  const loadedKey = useRef<string | null>(null);
  // The visitor asked to stay on the empty couple-of-days view. A new place clears it.
  const holdNarrowRef = useRef(false);
  const seenOrigin = useRef<string | null>(null);

  const applyFilters = useCallback(
    (update: FilterState | ((current: FilterState) => FilterState)) => {
      const next = typeof update === 'function' ? update(filters) : update;
      // Choosing the landing search again is a request to see it, even when it is empty.
      if (isDefaultLanding(next) && !isDefaultLanding(filters)) holdNarrowRef.current = true;
      for (const change of filterChanges(filters, next)) {
        trackFilterChange(change.name, change.value);
      }
      setFilters(next);
    },
    [filters],
  );

  useEffect(() => {
    if (consent !== 'accepted') return;
    initAnalytics();
    trackPageView(currentViewPath());
  }, [consent]);

  const loadEvents = useCallback(
    async (coords: { lat: number; lng: number }, requested: FallbackScope, allowAuto: boolean) => {
      const gen = ++loadGen.current;
      setFetchState('loading');
      try {
        const { scope, page: result } = await resolveEmptyFallback(requested, allowAuto, (next) =>
          api.getEvents({
            lat: coords.lat,
            lng: coords.lng,
            radiusMiles: next.radiusMiles,
            windowDays: next.windowDays,
          }),
        );
        if (loadGen.current !== gen) return;
        loadedKey.current = queryKey(coords, scope);
        setPage(result);
        setFallback(fallbackNoticeFor(requested, scope));
        setFetchState('ok');
        if (
          scope.radiusMiles !== requested.radiusMiles ||
          scope.windowDays !== requested.windowDays ||
          scope.when !== requested.when
        ) {
          setFilters((current) => ({
            ...current,
            radiusMiles: scope.radiusMiles,
            windowDays: scope.windowDays,
            when: scope.when,
          }));
        }
      } catch (error) {
        if (loadGen.current !== gen) return;
        const online = typeof navigator === 'undefined' ? null : navigator.onLine;
        setLoadError(classifyLoadError(error, online));
        setFetchState('error');
      }
    },
    [],
  );

  // Reload when the query origin or a query-affecting filter changes.
  // GPS/geocode in flight keep the previous coordinates, so the list stays.
  // Only radiusMiles/windowDays affect the server query. Offer, audience,
  // confirmed-food, happening-now, and sort are applied client-side and
  // deliberately do NOT trigger a refetch.
  // An empty default (next couple of days, 5 miles) walks out to 2 weeks,
  // then to a wider radius, inside that one load. The chip sync does not
  // refetch. The default origin is not written to sessionStorage.
  const { lat, lng } = locState.coordinates;
  const { radiusMiles, windowDays, when } = filters;
  const originKey = `${lat},${lng}`;
  useEffect(() => {
    if (seenOrigin.current !== originKey) {
      seenOrigin.current = originKey;
      holdNarrowRef.current = false;
      loadedKey.current = null;
      // A new place starts from the landing search again. The wider chips were
      // ours, not a choice the visitor made at that place.
      if (fallback) {
        setFilters((current) => ({
          ...current,
          when: DEFAULT_FILTER_STATE.when,
          windowDays: DEFAULT_FILTER_STATE.windowDays,
          radiusMiles: DEFAULT_FILTER_STATE.radiusMiles,
        }));
        setFallback(null);
        return;
      }
    }
    const requested: FallbackScope = { radiusMiles, windowDays, when };
    const key = queryKey({ lat, lng }, requested);
    if (loadedKey.current === key) return;
    void loadEvents({ lat, lng }, requested, !holdNarrowRef.current && isDefaultLanding(requested));
  }, [originKey, lat, lng, loadEvents, radiusMiles, windowDays, when, fallback]);

  // Move focus to the results heading after a later list loads. The first
  // settlement is left alone so the keyboard still starts at the location prompt.
  const focusMemory = useRef(INITIAL_RESULTS_FOCUS);
  useEffect(() => {
    const decision = nextResultsFocus(focusMemory.current, {
      status: fetchState,
      responseId: page?.responseId ?? null,
    });
    focusMemory.current = decision.memory;
    if (!decision.focus) return;
    document.getElementById(RESULTS_HEADING_ID)?.focus();
  }, [fetchState, page]);

  const onUseGps = useCallback(() => {
    const requestId = ++requestGen.current;
    dispatch({ type: 'request_gps' });
    if (!navigator.geolocation) {
      dispatch({ type: 'gps_unavailable' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (requestGen.current !== requestId) return;
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        if (storage) saveSessionLocation(storage, coords, 'gps', Date.now());
        dispatch({ type: 'gps_success', ...coords });
        trackUseMyLocation('granted');
      },
      (err) => {
        if (requestGen.current !== requestId) return;
        const denied = err.code === err.PERMISSION_DENIED;
        dispatch({ type: denied ? 'gps_denied' : 'gps_unavailable' });
        if (denied) trackUseMyLocation('denied');
      },
    );
  }, [storage]);

  const onManualSubmit = useCallback(
    async (placeText: string) => {
      const requestId = ++requestGen.current;
      dispatch({ type: 'manual_submit' });
      // The typed place stays on the device. Analytics only learns that a search ran.
      trackFilterChange('location', 'search');
      try {
        const coords = await api.geocode(placeText);
        if (requestGen.current !== requestId) return;
        if (!coords || sameCoordinates(coords, locRef.current.coordinates)) {
          trackLocationSearch('unresolved');
          dispatch({ type: 'manual_unresolved' });
          return;
        }
        trackLocationSearch('resolved');
        if (storage) saveSessionLocation(storage, coords, 'manual', Date.now(), placeText);
        dispatch({ type: 'manual_resolved', ...coords, label: placeText });
      } catch {
        if (requestGen.current !== requestId) return;
        trackLocationSearch('unresolved');
        dispatch({ type: 'manual_unresolved' });
      }
    },
    [storage],
  );

  const onClear = useCallback(() => {
    requestGen.current += 1;
    if (storage) clearSessionLocation(storage);
    dispatch({ type: 'clear' });
  }, [storage]);

  const onImpressions = useCallback((tokens: string[]) => {
    void api.submitImpressions(tokens);
  }, []);

  // Distance and the date window stay. Those drive the server query and have
  // their own empty state. This only lifts the filters that run in the browser.
  const returnToNarrow = useCallback(
    (notice: FallbackNotice) => {
      holdNarrowRef.current = true;
      applyFilters((current) => ({
        ...current,
        when: 'couple',
        windowDays: notice.fromDays,
        radiusMiles: notice.fromMiles,
      }));
    },
    [applyFilters],
  );

  const clearClientFilters = useCallback(() => {
    applyFilters((current) => ({
      ...current,
      offer: 'either',
      hideRestricted: false,
      confirmedOnly: false,
      happeningNow: false,
    }));
  }, [applyFilters]);

  const chooseConsent = (choice: ConsentChoice) => {
    storeConsentChoice(choice);
    setConsent(choice);
  };

  return (
    <main className="app">
      <header className="hero">
        <h1>Free Events Finder</h1>
        <p className="tagline">Free Seattle events with free food and drinks.</p>
        <SourcesLine names={page ? collectSourceNames(page.events) : []} settled={page !== null} />
      </header>

      {consent === null ? (
        <ConsentBanner
          onAccept={() => chooseConsent('accepted')}
          onDecline={() => chooseConsent('declined')}
        />
      ) : null}

      <LocationPrompt
        state={locState}
        onUseGps={onUseGps}
        onManualSubmit={onManualSubmit}
        onClear={onClear}
      />

      <Filters value={filters} onChange={applyFilters} />
      <section className="results" aria-live="polite">
        {fetchState === 'loading' ? <LoadingState /> : null}
        {fetchState === 'error' ? (
          <ErrorState
            kind={loadError}
            onRetry={() => {
              const requested: FallbackScope = {
                radiusMiles: filters.radiusMiles,
                windowDays: filters.windowDays,
                when: filters.when,
              };
              void loadEvents(
                locState.coordinates,
                requested,
                !holdNarrowRef.current && isDefaultLanding(requested),
              );
            }}
          />
        ) : null}
        {fetchState === 'ok' && page ? (
          <>
            <FreshnessIndicator updatedAt={page.lastSuccessfulPromotionAt} />
            <StaleNotice page={page} />
            {page.events.length === 0 ? (
              <EmptyState
                page={page}
                windowDays={filters.windowDays}
                onShowNextCoupleDays={() => {
                  applyFilters((current) => ({ ...current, when: 'couple', windowDays: 3 }));
                }}
                {...(filters.when === 'couple' && filters.windowDays < 14
                  ? {
                      onShowTwoWeeks: () => {
                        applyFilters((current) => ({
                          ...current,
                          when: 'two_weeks',
                          windowDays: 14,
                        }));
                      },
                    }
                  : {})}
                {...(fallback
                  ? {
                      onReturnToNarrow: () => returnToNarrow(fallback),
                      returnLabel: fallbackReturnLabel(fallback),
                    }
                  : {})}
                onWiden={(miles) => {
                  trackWidenRadius(miles);
                  applyFilters((current) => ({ ...current, radiusMiles: miles }));
                }}
              />
            ) : (
              <>
                {fallback ? (
                  <WiderWindowNotice
                    message={fallbackNoticeText(fallback)}
                    actionLabel={fallbackReturnLabel(fallback)}
                    onAction={() => returnToNarrow(fallback)}
                  />
                ) : null}
                <ResultsView
                  page={page}
                  filters={filters}
                  origin={locState.coordinates}
                  originLabel={originPinLabel(locState)}
                  distanceVoice={locState.source === 'gps' ? 'gps' : 'place'}
                  onImpressions={onImpressions}
                  onClearClientFilters={clearClientFilters}
                />
              </>
            )}
          </>
        ) : null}
      </section>

      <BuildFooter page={page} />
    </main>
  );
}

/**
 * Tiny build/version line. Makes it obvious whether the page you are looking at
 * is the latest deploy (web build) and which data generation the API served —
 * the two things you need when verifying a change actually shipped.
 */
function BuildFooter({ page }: { page: EventsPage | null }): JSX.Element {
  const build = import.meta.env?.VITE_BUILD_VERSION ?? 'dev';
  return (
    <footer className="build-footer">
      <p className="site-privacy">
        This site uses Google Analytics (cookies) and Cloudflare Web Analytics (cookieless) to
        understand which free events are popular. We don&apos;t sell data or run ads.
      </p>
      <p className="build-stamp">
        <span title="web build">build {build}</span>
        {page ? (
          <span title="data generation served by the API">
            {' '}
            · data {page.dataGenerationVersion}
          </span>
        ) : null}
      </p>
    </footer>
  );
}
